'use client';

import { useState, ReactNode, useRef, useEffect, useId } from 'react';
import { addDays, format, getDate, getISODay, parse } from 'date-fns';
import { Locale as DayPickerLocale } from 'react-day-picker';
import { enGB, de, fr, arSA } from 'react-day-picker/locale';
import { css, Z_INDEX_LIBRARY } from '@bts-web/utils-style-engine';
import { Locale, isRtlLanguageFromLocale } from '@bts-web/utils-lokalise';

const localeMap: Record<Locale, DayPickerLocale> = {
  'en-GB': enGB,
  'de-DE': de,
  'fr-FR': fr,
  'ar-AR': arSA,
};

// ISO weekday: Mon=1 ... Sun=7
const ISO_WEEKDAYS_INDEXES = [1, 2, 3, 4, 5, 6, 7] as const;

type ISOWeekDay = (typeof ISO_WEEKDAYS_INDEXES)[number];

// Reference Monday (Jan 1 2024 was a Monday)
const REFERENCE_MONDAY = new Date(2024, 0, 1);

function getWeekDayAbbreviation(
  isoDay: ISOWeekDay,
  dpLocale: DayPickerLocale,
): string {
  return format(addDays(REFERENCE_MONDAY, isoDay - 1), 'EEE', {
    locale: dpLocale as NonNullable<Parameters<typeof format>[2]>['locale'],
  });
}

// If the value was previously set by the weekly picker it will be day 1–7 of
// the month (the BE encoding). Otherwise (e.g. the initial MONTHLY default
// value) derive the weekday from the actual calendar day-of-week.
export function getWeekDayIndexFromYmd(ymd: string): ISOWeekDay {
  const date = parse(ymd, 'yyyy-MM-dd', new Date());

  if (isNaN(date.getTime())) {
    throw new Error(`getWeeklyDateForISODay - Invalid date format: ${ymd}`);
  }

  const dayOfMonth = getDate(date);

  if (dayOfMonth >= 1 && dayOfMonth <= 7) {
    return dayOfMonth as ISOWeekDay;
  }

  return getISODay(date) as ISOWeekDay;
}

// Maps ISO weekday 1–7 to the corresponding date in the current month,
// which is what the BE expects for weekly savings plans.
function getWeeklyDateForISODay(isoDay: ISOWeekDay): string {
  const now = new Date();

  return format(
    new Date(now.getFullYear(), now.getMonth(), isoDay),
    'yyyy-MM-dd',
  );
}

export function getWeekDayNameFromYmd(ymd: string, locale: Locale): string {
  const dpLocale = localeMap[locale];

  const isoDay = getWeekDayIndexFromYmd(ymd);

  return format(addDays(REFERENCE_MONDAY, isoDay - 1), 'EEEE', {
    locale: dpLocale as NonNullable<Parameters<typeof format>[2]>['locale'],
  });
}

export const WeekDayPicker = ({
  value,
  onChange,
  cancelLabel,
  confirmLabel,
  locale,
  children,
  title,
  zIndex = Z_INDEX_LIBRARY.MODAL,
}: {
  value: string;
  onChange: (date: string) => void;
  cancelLabel: string;
  confirmLabel: string;
  locale: Locale;
  children: ReactNode;
  title?: string;
  zIndex?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [selectedDay, setSelectedDay] = useState<ISOWeekDay | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const titleId = useId();

  const dpLocale = localeMap[locale];

  const isRTL = isRtlLanguageFromLocale(locale);

  const currentISODay = getWeekDayIndexFromYmd(value);

  const activeDay = selectedDay ?? currentISODay;

  const handleOpen = () => {
    setSelectedDay(currentISODay);

    setIsOpen(true);
  };

  const handleConfirm = () => {
    onChange(getWeeklyDateForISODay(activeDay));

    setSelectedDay(null);

    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedDay(null);

    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const el = contentRef.current;

    if (!el) return;

    el.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();

        return;
      }

      if (e.key === 'Tab') {
        const focusable = Array.from(
          el.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (focusable.length === 0) return;

        const first = focusable[0];

        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();

            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();

            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      <button
        aria-label="Select day of week"
        type="button"
        data-element="date-picker-open-trigger"
        onClick={handleOpen}
      >
        {children}
      </button>

      {isOpen && (
        <>
          <div
            aria-hidden="true"
            className={css({
              position: 'fixed',
              inset: '0',
              backgroundColor: 'rgba(166, 166, 166, 0.50)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw',
              overflow: 'hidden',
            })}
            data-testid="week-day-picker-overlay"
            style={{ zIndex }}
            onClick={handleCancel}
          />

          <div
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : 'Select day of week'}
            tabIndex={-1}
            style={{ zIndex }}
            data-testid="week-day-picker-content"
            className={css({
              position: 'absolute',
              bottom: 'large',
              width: '358px',
              maxWidth: '100%',
              insetInline: 0,
              margin: '0 auto',
              backgroundColor: 'neutrals.card_fill_primary',
              borderEndEndRadius: 'smallBottomRight',
              borderEndStartRadius: 'smallTopRight',
              borderStartStartRadius: 'smallTopLeft',
              borderStartEndRadius: 'smallBottomLeft',
              p: 'small',
            })}
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {title && (
              <span
                id={titleId}
                className={css({
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: '0',
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0,0,0,0)',
                  whiteSpace: 'nowrap',
                  border: '0',
                })}
              >
                {title}
              </span>
            )}
            <div
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'extra_small_3',
                py: 'small',
              })}
            >
              {ISO_WEEKDAYS_INDEXES.map((day) => {
                const isSelected = day === activeDay;

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDay(day)}
                    aria-pressed={isSelected}
                    className={css({
                      flex: 1,
                      height: '40px',
                      borderRadius: '22px',
                      ...(isSelected
                        ? {
                            backgroundColor: 'information.fill_focused',
                            color: 'information.fill_primary',
                            fontSize: 'headline.small_medium',
                            fontWeight: 'headline.small_medium',
                            lineHeight: 'headline.small_medium',
                            letterSpacing: 'headline.small_medium',
                          }
                        : {
                            fontSize: 'body.medium',
                            fontWeight: 'body.medium',
                            letterSpacing: 'body.medium',
                            lineHeight: 'body.medium',
                            color: 'neutrals.text_primary',
                          }),
                    })}
                  >
                    {getWeekDayAbbreviation(day, dpLocale)}
                  </button>
                );
              })}
            </div>

            <div
              data-element="date-picker-footer"
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 'extra_small',
                '& button': {
                  color: 'information.fill_primary',
                  height: '32px',
                },
                '& > button:first-child': {
                  fontSize: 'label.medium_medium',
                  lineHeight: 'label.medium_medium',
                  letterSpacing: 'label.medium_medium',
                  fontWeight: 'label.medium_medium',
                },
                '& > button:last-child': {
                  fontSize: 'label.large_bold',
                  fontWeight: 'label.large_bold',
                  lineHeight: 'label.large_bold',
                  letterSpacing: 'label.large_bold',
                },
              })}
            >
              <button
                type="button"
                onClick={handleCancel}
                data-testid="week-day-picker-cancel"
              >
                {cancelLabel}
              </button>

              <button
                type="button"
                onClick={handleConfirm}
                data-testid="week-day-picker-confirm"
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
